import { ApolloLink, InMemoryCache, split, TypePolicies } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { PhAuthService } from '@common/ph-auth';
import type { NamedOptions } from 'apollo-angular';
import { APOLLO_NAMED_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { createClient } from 'graphql-ws';
import { firstValueFrom } from 'rxjs';
import { PhConfigLoaderService } from '../common/ph-config-loader';
import ApiGatewayPolicies from '../queries/api-gateway/_client-type-policies';
import CentralAdminPolicies from '../queries/central-admin/_client-type-policies';

type CreateApolloOptions = {
  socketUrl?: string;
  typePolicies?: TypePolicies;
};

function createApollo(name: string, url: string, options: CreateApolloOptions = {}) {
  const socketUrl = options.socketUrl || url.replace(/^.*?(?=\/\/)/, 'ws');
  const cache = new InMemoryCache({ typePolicies: options.typePolicies });

  return function (httpLink: HttpLink, auth0Service: PhAuthService) {
    const auth = setContext(() =>
      firstValueFrom(auth0Service.token$).then(token => ({
        headers: { Authorization: `Bearer ${token}` },
      }))
    );
    const http = httpLink.create({ uri: url });
    const ws = new GraphQLWsLink(createClient({ url: socketUrl }));

    const link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      ws,
      ApolloLink.from([auth, http])
    );

    return { link, cache, name };
  };
}

function createNamed(mapping: Record<string, [string, CreateApolloOptions?]>) {
  return function (httpLink: HttpLink, auth0Service: PhAuthService) {
    const named: NamedOptions = {};
    for (let name in mapping) {
      named[name] = createApollo(name, ...mapping[name])(httpLink, auth0Service);
    }
    return named;
  };
}

export const APOLLO_CONFIG = {
  provide: APOLLO_NAMED_OPTIONS,
  useFactory: (configLoader: PhConfigLoaderService, httpLink: HttpLink, authService: PhAuthService) =>
    createNamed({
      CA: [configLoader.getConfig('CA_GQL_API_URL'), { typePolicies: CentralAdminPolicies }],
      API: [configLoader.getConfig('API_GATEWAY_URL'), { typePolicies: ApiGatewayPolicies }],
    })(httpLink, authService),
  deps: [PhConfigLoaderService, HttpLink, PhAuthService],
};
